import React, { useState } from 'react';
import { useAssets, useDeleteAsset, useDeactivateAsset, useUpdateAsset } from '../../hooks/useAssets';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import LoadingSpinner from '../common/LoadingSpinner';
import AssetForm from './AssetForm';
import AuditDrawer from '../audit/AuditDrawer';
import type { AssetType, Asset } from '../../types/asset';
import { Pencil, Trash2, Archive, CheckCircle, History } from 'lucide-react';

interface AssetListProps {
  assetType: AssetType;
}

const AssetList: React.FC<AssetListProps> = ({ assetType }) => {
  const { data: assets, isLoading } = useAssets({ assetTypeId: assetType.assetTypeId, activeOnly: false });
  const deleteAsset = useDeleteAsset();
  const deactivateAsset = useDeactivateAsset();
  const updateAsset = useUpdateAsset();
  
  const [showForm, setShowForm] = useState(false);
  const [editingAsset, setEditingAsset] = useState<Asset | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);
  const [showActivateConfirm, setShowActivateConfirm] = useState(false);
  const [assetToDelete, setAssetToDelete] = useState<Asset | null>(null);
  const [assetToDeactivate, setAssetToDeactivate] = useState<Asset | null>(null);
  const [assetToActivate, setAssetToActivate] = useState<Asset | null>(null);
  const [auditDrawer, setAuditDrawer] = useState<{
    isOpen: boolean;
    assetId: string;
    displayName: string;
  }>({
    isOpen: false,
    assetId: '',
    displayName: '',
  });

  const handleCreate = () => {
    setEditingAsset(null);
    setShowForm(true);
  };

  const handleEdit = (asset: Asset) => {
    setEditingAsset(asset);
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingAsset(null);
  };

  const handleDeleteClick = (asset: Asset) => {
    setAssetToDelete(asset);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (assetToDelete) {
      await deleteAsset.mutateAsync(assetToDelete.assetId);
      setShowDeleteConfirm(false);
      setAssetToDelete(null);
    }
  };

  const handleDeactivateClick = (asset: Asset) => {
    setAssetToDeactivate(asset);
    setShowDeactivateConfirm(true);
  };

  const handleConfirmDeactivate = async () => {
    if (assetToDeactivate) {
      await deactivateAsset.mutateAsync(assetToDeactivate.assetId);
      setShowDeactivateConfirm(false);
      setAssetToDeactivate(null);
    }
  };

  const handleActivateClick = (asset: Asset) => {
    setAssetToActivate(asset);
    setShowActivateConfirm(true);
  };

  const handleConfirmActivate = async () => {
    if (assetToActivate) {
      await updateAsset.mutateAsync({
        assetId: assetToActivate.assetId,
        data: { isActive: true }
      });
      setShowActivateConfirm(false);
      setAssetToActivate(null);
    }
  };

  const handleViewHistory = (asset: Asset) => {
    setAuditDrawer({
      isOpen: true,
      assetId: asset.assetId,
      displayName: asset.displayName,
    });
  };

  const handleCloseHistory = () => {
    setAuditDrawer({
      isOpen: false,
      assetId: '',
      displayName: '',
    });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const activeAssets = assets?.filter(a => a.isActive) || [];
  const inactiveAssets = assets?.filter(a => !a.isActive) || [];

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{assetType.name}</h3>
          <p className="text-sm text-gray-600">
            {activeAssets.length} {activeAssets.length === 1 ? 'activo' : 'activos'}
            {inactiveAssets.length > 0 && ` • ${inactiveAssets.length} desactivado${inactiveAssets.length !== 1 ? 's' : ''}`}
          </p>
        </div>
        <Button onClick={handleCreate}>
          + Nuevo {assetType.nameSingular}
        </Button>
      </div>

      {activeAssets.length === 0 && inactiveAssets.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-gray-600 mb-4">
              No hay {assetType.name.toLowerCase()} registrados
            </p>
            <Button onClick={handleCreate}>
              Crear Primer {assetType.nameSingular}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nombre</TableHead>
                  {assetType.fields.map(field => (
                    <TableHead key={field.fieldId}>{field.fieldName}</TableHead>
                  ))}
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {activeAssets.map((asset) => (
                  <TableRow key={asset.assetId}>
                    <TableCell className="font-medium">{asset.displayName}</TableCell>
                    {assetType.fields.map(field => (
                      <TableCell key={field.fieldId}>
                        {asset.fieldValues[field.fieldId] || '-'}
                      </TableCell>
                    ))}
                    <TableCell>
                      <Badge variant="default">Activo</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleViewHistory(asset)}
                          title="Ver historial"
                        >
                          <History className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEdit(asset)}
                          title="Editar"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeactivateClick(asset)}
                          title="Desactivar"
                        >
                          <Archive className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}

                {inactiveAssets.length > 0 && (
                  <>
                    <TableRow>
                      <TableCell colSpan={assetType.fields.length + 3} className="bg-gray-50">
                        <span className="text-sm font-medium text-gray-500">
                          Activos Desactivados
                        </span>
                      </TableCell>
                    </TableRow>
                    {inactiveAssets.map((asset) => (
                      <TableRow key={asset.assetId} className="opacity-60">
                        <TableCell className="font-medium">{asset.displayName}</TableCell>
                        {assetType.fields.map(field => (
                          <TableCell key={field.fieldId}>
                            {asset.fieldValues[field.fieldId] || '-'}
                          </TableCell>
                        ))}
                        <TableCell>
                          <Badge variant="secondary">Desactivado</Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleViewHistory(asset)}
                              title="Ver historial"
                            >
                              <History className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleActivateClick(asset)}
                              title="Activar"
                              className="text-green-600 hover:text-green-700 hover:bg-green-50"
                            >
                              <CheckCircle className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteClick(asset)}
                              title="Eliminar permanentemente"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Form Dialog */}
      {showForm && (
        <AssetForm
          assetType={assetType}
          asset={editingAsset}
          open={showForm}
          onClose={handleCloseForm}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Eliminación</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-center text-gray-700">
              ¿Estás seguro de que deseas eliminar permanentemente{' '}
              <span className="font-semibold">{assetToDelete?.displayName}</span>?
            </p>
            <p className="text-sm text-red-600 text-center">
              Esta acción no se puede deshacer.
            </p>
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancelar
              </Button>
              <Button
                variant="destructive"
                onClick={handleConfirmDelete}
                disabled={deleteAsset.isLoading}
              >
                {deleteAsset.isLoading ? 'Eliminando...' : 'Eliminar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Deactivate Confirmation Dialog */}
      <Dialog open={showDeactivateConfirm} onOpenChange={setShowDeactivateConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Desactivación</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-center text-gray-700">
              ¿Estás seguro de que deseas desactivar{' '}
              <span className="font-semibold">{assetToDeactivate?.displayName}</span>?
            </p>
            <p className="text-sm text-gray-600 text-center">
              Podrás reactivarlo más tarde si lo necesitas.
            </p>
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setShowDeactivateConfirm(false)}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleConfirmDeactivate}
                disabled={deactivateAsset.isLoading}
              >
                {deactivateAsset.isLoading ? 'Desactivando...' : 'Desactivar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Activate Confirmation Dialog */}
      <Dialog open={showActivateConfirm} onOpenChange={setShowActivateConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Activación</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-center text-gray-700">
              ¿Estás seguro de que deseas activar{' '}
              <span className="font-semibold">{assetToActivate?.displayName}</span>?
            </p>
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setShowActivateConfirm(false)}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleConfirmActivate}
                disabled={updateAsset.isLoading}
                className="bg-green-600 hover:bg-green-700"
              >
                {updateAsset.isLoading ? 'Activando...' : 'Activar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Audit Drawer */}
      <AuditDrawer
        entityType="ASSET"
        entityId={auditDrawer.assetId}
        entityName={auditDrawer.displayName}
        isOpen={auditDrawer.isOpen}
        onClose={handleCloseHistory}
      />
    </div>
  );
};

export default AssetList;
